import { useState, useEffect, useRef } from "react";
import { dashboardService } from "@/services/dashboardService";
import type { Alert, TeacherStats } from "@/services/dashboardService";
import {
  FileBarChart2, AlertTriangle, CheckCircle, TrendingDown,
  Download, Calendar, Users, Shield,
} from "lucide-react";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { useTeacherNavigation } from "@/contexts/TeacherNavigationContext";

function fmtResponseTime(minutes: number | undefined | null): string {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m avg response`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m avg response` : `${h}h avg response`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h avg response` : `${d}d avg response`;
}

function weekStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  return d;
}

function fmt(n: number, singular: string, plural = singular + 's') {
  return `${n} ${n === 1 ? singular : plural}`;
}

function topCategory(alerts: Alert[]): string {
  if (alerts.length === 0) return 'None';
  const counts: Record<string, number> = {};
  for (const a of alerts) {
    const t = a.alertType || 'other';
    counts[t] = (counts[t] ?? 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function topStudent(alerts: Alert[]): { name: string; alertCount: number } {
  if (alerts.length === 0) return { name: 'None', alertCount: 0 };
  const counts: Record<string, number> = {};
  for (const a of alerts) {
    const n = a.studentName || 'Unknown';
    counts[n] = (counts[n] ?? 0) + 1;
  }
  const [name, count] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return { name, alertCount: count };
}

interface SummaryCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}
function SummaryCard({ icon: Icon, label, value, sub, color }: SummaryCardProps) {
  return (
    <div className={`flex items-start gap-4 p-5 rounded-xl border shadow-sm`}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-800">{value}</p>
        <p className="text-sm font-medium text-slate-600">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export function Reports() {
  const { dm } = useTeacherTheme();
  const { setCurrentPage } = useTeacherNavigation();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<TeacherStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([
      dashboardService.getTeacherAlerts(1, 200),
      dashboardService.getTeacherStats(),
    ])
      .then(([alertsRes, statsRes]) => {
        setAlerts(alertsRes.data);
        setStats(statsRes);
      })
      .catch(() => setError('Could not load report data.'))
      .finally(() => setLoading(false));
  }, []);

  const ws = weekStart();
  const thisWeek = alerts.filter(a => new Date(a.createdAt) >= ws);
  const resolved = thisWeek.filter(a => a.status === 'resolved');
  const unresolved = thisWeek.filter(a => a.status !== 'resolved' && a.status !== 'dismissed');
  const critical = thisWeek.filter(a => a.severity === 'high');
  const resolutionRate = thisWeek.length > 0 ? Math.round((resolved.length / thisWeek.length) * 100) : 0;

  const handlePrint = () => {
    window.print();
  };

  const dateRange = `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Generating report…</div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-400 text-sm">{error}</div>
    );
  }

  return (
    <div className="space-y-6" ref={printRef}>
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Weekly Safety Report</h2>
          <div className={`flex items-center gap-1.5 mt-0.5 text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            <Calendar className="h-3.5 w-3.5" />
            <span>{dateRange}</span>
          </div>
        </div>
        <button
          onClick={handlePrint}
          className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export PDF
        </button>
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={AlertTriangle}
          label="Alerts This Week"
          value={thisWeek.length}
          sub={fmt(critical.length, 'critical')}
          color="bg-red-500"
        />
        <SummaryCard
          icon={CheckCircle}
          label="Resolved"
          value={resolved.length}
          sub={`${resolutionRate}% resolution rate`}
          color="bg-emerald-500"
        />
        <SummaryCard
          icon={TrendingDown}
          label="Unresolved"
          value={unresolved.length}
          sub="requiring action"
          color="bg-amber-500"
        />
        <SummaryCard
          icon={Users}
          label="Students Monitored"
          value={stats?.studentsMonitored ?? '—'}
          sub={fmtResponseTime(stats?.avgResponseTime)}
          color="bg-blue-600"
        />
      </div>

      {/* Top insights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`p-5 rounded-xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>Top Alert Category</h3>
          <p className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{topCategory(thisWeek)}</p>
          <p className={`text-xs mt-1 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Most common concern this week</p>
        </div>
        <div className={`p-5 rounded-xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>Most Flagged Student</h3>
          {(() => {
            const topStudentData = topStudent(thisWeek);
            return topStudentData.name === 'None' ? (
              <p className="text-xl font-bold" style={{ color: dm ? '#e2e8f0' : '#000000' }}>None</p>
            ) : (
              <button
                onClick={() => setCurrentPage('studentsMonitored')}
                className="text-xl font-bold transition-colors"
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  padding: 0,
                  color: dm ? '#e2e8f0' : '#000000'
                }}
                title={`Click to view ${topStudentData.name}'s profile`}
              >
                {topStudentData.name}
              </button>
            );
          })()}
          <p className={`text-xs mt-1 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
            {(() => {
              const topStudentData = topStudent(thisWeek);
              return topStudentData.name === 'None' 
                ? 'No alerts this week' 
                : `${topStudentData.alertCount} alert${topStudentData.alertCount === 1 ? '' : 's'} this week`;
            })()}
          </p>
        </div>
      </div>

      {/* Severity breakdown */}
      <div className={`p-5 rounded-xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>Severity Breakdown — This Week</h3>
        {(['high', 'medium', 'low'] as const).map(sev => {
          const count = thisWeek.filter(a => a.severity === sev).length;
          const pct = thisWeek.length > 0 ? Math.round((count / thisWeek.length) * 100) : 0;
          const colors = {
            high: { bar: 'bg-red-500', label: 'High', text: 'text-red-600' },
            medium: { bar: 'bg-amber-400', label: 'Medium', text: 'text-amber-600' },
            low: { bar: 'bg-emerald-400', label: 'Low', text: 'text-emerald-600' },
          };
          return (
            <div key={sev} className="flex items-center gap-3 mb-3 last:mb-0">
              <span className={`w-14 text-xs font-medium ${colors[sev].text}`}>{colors[sev].label}</span>
              <div className={`flex-1 h-3 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <div
                  className={`h-full rounded-full ${colors[sev].bar} transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className={`w-16 text-right text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{count} ({pct}%)</span>
            </div>
          );
        })}
      </div>

      {/* Recent resolved alerts */}
      <div className={`p-5 rounded-xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>Recently Resolved This Week</h3>
        {resolved.length === 0 ? (
          <p className={`text-sm ${dm ? 'text-slate-500' : 'text-slate-400'}`}>No resolved alerts yet this week</p>
        ) : (
          <div className="space-y-2.5">
            {resolved.slice(0, 8).map(a => (
              <div key={a.id} className="flex items-center gap-3">
                <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm truncate ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{a.studentName}</span>
                  <span className={`text-xs ml-2 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{a.alertType?.replace(/_/g, ' ')}</span>
                </div>
                <span className="text-xs text-slate-400 shrink-0">
                  {new Date(a.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
            {resolved.length > 8 && (
              <p className={`text-xs pt-1 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>+{resolved.length - 8} more resolved alerts</p>
            )}
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className={`flex items-center gap-2 text-xs py-2 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
        <Shield className="h-3.5 w-3.5" />
        <span>Report generated by ShieldED Haven · {new Date().toLocaleString()}</span>
        <FileBarChart2 className="h-3.5 w-3.5 ml-auto" />
      </div>
    </div>
  );
}
